import { useRef, useCallback } from 'react';
import { createScopedLogger } from '../../utils/debug';

// Define the expected structure for decoded data
interface DecodedData {
  href: string;
  origin: string;
}

export function useStegaElements() {
  const debug = createScopedLogger('useStegaElements');
  const elementsRef = useRef<NodeListOf<Element> | undefined>(undefined);
  const observerRef = useRef<MutationObserver | null>(null);

  const getElements = useCallback(() => {
    if (!elementsRef.current) {
      elementsRef.current = document.querySelectorAll('[data-prepr-encoded]');
    }
    return elementsRef.current;
  }, []);

  const scanNode = useCallback(
    (node: Node, decode: (str: string | null) => DecodedData | null) => {
      function scan(n: Node, d: (str: string | null) => DecodedData | null) {
        if (n.nodeType === Node.TEXT_NODE) {
          if (!n.textContent?.trim()) return;
          if (n.parentElement?.closest('script, style, noscript')) return;
          const decoded = d(n.textContent);
          if (decoded?.href) {
            const target = n.parentElement;
            if (target && !target.hasAttribute('data-prepr-encoded')) {
              target.setAttribute('data-prepr-encoded', '');
              target.setAttribute('data-prepr-href', decoded.href);
              target.setAttribute('data-prepr-origin', decoded.origin);
              debug.log('encoded element found:', {
                href: decoded.href,
                origin: decoded.origin,
              });
            }
          }
        } else if (n.nodeType === Node.ELEMENT_NODE) {
          for (let i = 0; i < n.childNodes.length; i++) {
            scan(n.childNodes[i], d);
          }
        }
      }
      scan(node, decode);
    },
    [debug]
  );

  const scanDocument = useCallback(
    (
      decode: (str: string | null) => DecodedData | null,
      skipIfTagged: boolean = false
    ) => {
      debug.log('starting document scan', { skipIfTagged });

      // Skip re-scanning if elements are already tagged and skipIfTagged is true
      if (skipIfTagged) {
        const existingElements = document.querySelectorAll(
          '[data-prepr-encoded]'
        );
        if (existingElements.length > 0) {
          debug.log(
            'skipping scan, found',
            existingElements.length,
            'pre-tagged elements'
          );
          elementsRef.current = existingElements;
          return;
        }
      }

      const walker = document.createTreeWalker(
        document.body,
        NodeFilter.SHOW_TEXT,
        {
          acceptNode: node => {
            if (node.parentElement?.closest('script, style, noscript')) {
              return NodeFilter.FILTER_REJECT;
            }
            if (!node.textContent?.trim()) {
              return NodeFilter.FILTER_REJECT;
            }
            return NodeFilter.FILTER_ACCEPT;
          },
        }
      );
      let textNode;
      let encodedCount = 0;
      while ((textNode = walker.nextNode())) {
        const decoded = decode(textNode.textContent);
        if (decoded?.href) {
          const target = textNode.parentElement;
          if (target && !target.hasAttribute('data-prepr-encoded')) {
            target.setAttribute('data-prepr-encoded', '');
            target.setAttribute('data-prepr-href', decoded.href);
            target.setAttribute('data-prepr-origin', decoded.origin);
            encodedCount++;
          }
        }
      }
      debug.log('document scan complete, encoded', encodedCount, 'elements');
      elementsRef.current = document.querySelectorAll('[data-prepr-encoded]');
    },
    [debug]
  );

  const setupMutationObserver = useCallback(
    (
      decode: (str: string | null) => DecodedData | null,
      onUpdate?: () => void
    ) => {
      if (observerRef.current) {
        observerRef.current.disconnect();
      }
      let pendingMutations: MutationRecord[] = [];
      let debounceTimeout: NodeJS.Timeout | null = null;
      const processMutations = () => {
        const allAddedNodes = new Set<Node>();
        pendingMutations.forEach(mutation => {
          mutation.addedNodes.forEach(node => allAddedNodes.add(node));
          // Include text changes in existing nodes
          if (mutation.type === 'characterData') {
            allAddedNodes.add(mutation.target);
          }
        });
        allAddedNodes.forEach(node => scanNode(node, decode));
        pendingMutations = [];
        elementsRef.current = document.querySelectorAll('[data-prepr-encoded]');
        if (onUpdate) onUpdate();
      };
      observerRef.current = new MutationObserver(mutations => {
        pendingMutations.push(...mutations);
        if (debounceTimeout) clearTimeout(debounceTimeout);
        debounceTimeout = setTimeout(processMutations, 100);
      });
      observerRef.current.observe(document.body, {
        childList: true,
        subtree: true,
        characterData: true,
      });
      debug.log('mutation observer set up');
    },
    [scanNode, debug]
  );

  const cleanupVisuals = useCallback(() => {
    // Remove only visual highlights, preserve data attributes
    const activeElements = document.querySelectorAll('.prepr-overlay-active');
    activeElements.forEach(element => {
      element.classList.remove('prepr-overlay-active');
    });
    debug.log('cleaned up visuals for', activeElements.length, 'elements');
  }, [debug]);

  const cleanupAll = useCallback(() => {
    if (observerRef.current) {
      observerRef.current.disconnect();
      observerRef.current = null;
    }
    const encodedElements = document.querySelectorAll('[data-prepr-encoded]');
    encodedElements.forEach(element => {
      element.removeAttribute('data-prepr-encoded');
      element.removeAttribute('data-prepr-href');
      element.removeAttribute('data-prepr-origin');
      element.classList.remove('prepr-overlay-active');
    });
    debug.log('cleaned up all for', encodedElements.length, 'encoded elements');
    elementsRef.current = undefined;
  }, [debug]);

  const cleanup = useCallback(() => {
    // For backward compatibility, cleanup calls cleanupAll
    cleanupAll();
  }, [cleanupAll]);

  return {
    getElements,
    scanDocument,
    setupMutationObserver,
    cleanup,
    cleanupVisuals,
    cleanupAll,
  };
}
